import React, { useState, useEffect } from 'react';
import './RunningTab.css';

interface RunningActivity {
  activityId: number;
  activityName: string;
  startTimeLocal: string;
  startTimeGMT: string;
  distance?: number;
  duration?: number;
  elevationGain?: number;
  averageHR?: number;
  maxHR?: number;
  calories?: number;
  activityTypeKey?: string;
}

interface ActivityGroup {
  date: string; // YYYY-MM-DD
  activities: RunningActivity[];
}

interface MergedStravaActivity {
  id: number;
  name: string;
  startTimeGMT: string;
  startTimeLocal: string;
  distance: number;
  duration: number;
  elevationGain?: number;
  averageHR?: number;
  maxHR?: number;
  calories?: number;
  averageSpeed?: number;
  maxSpeed?: number;
  averageCadence?: number;
  averageWatts?: number;
  kilojoules?: number;
  deviceWatts?: boolean;
  sufferScore?: number;
  type: string;
}

interface RunGroup {
  activities: RunningActivity[];
  date: string; // YYYY-MM-DD
  mergedStravaActivity?: MergedStravaActivity;
  validation?: {
    allClearedFromStrava: boolean;
    stravaMatches: Record<string, number | null>; // garminActivityId -> stravaActivityId
    error?: string;
  };
}

interface MergeStatus {
  groupIndex: number;
  status: 'idle' | 'merging' | 'success' | 'error' | 'recovery' | 'validating';
  step?: string;
  error?: string;
  canRetry?: boolean;
  needsRecovery?: boolean;
  missingIds?: number[];
}

interface RunningTabProps {
  refreshTrigger: number;
  isConnected: boolean;
  isSyncing: boolean;
  onDryRun: () => void;
  onSync: () => void;
}

export const RunningTab: React.FC<RunningTabProps> = ({ isConnected, isSyncing }) => {
  const [isSyncLoading, setIsSyncLoading] = useState(false);
  const [isActivitiesLoading, setIsActivitiesLoading] = useState(false);
  const [activityGroups, setActivityGroups] = useState<ActivityGroup[]>([]);
  const [runGroups, setRunGroups] = useState<RunGroup[]>([]);
  const [mergeStatuses, setMergeStatuses] = useState<Record<number, MergeStatus>>({});
  const [syncResult, setSyncResult] = useState<{
    activitiesSynced: number;
    activitiesDeleted: number;
    isFirstSync: boolean;
    syncPeriodDays: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load activities on mount
  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async () => {
    setIsActivitiesLoading(true);
    setError(null);

    try {
      const headers: Record<string, string> = {};
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch('/api/running/activities', {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to load activities');
      }

      const data = await response.json();
      setActivityGroups(data.activityGroups || []);
      setRunGroups(data.mergeGroups || []);
    } catch (err) {
      console.error('Error loading activities:', err);
      setError('Failed to load activities. Please try syncing first.');
    } finally {
      setIsActivitiesLoading(false);
    }
  };

  const handleSync = async () => {
    setIsSyncLoading(true);
    setError(null);
    setSyncResult(null);

    try {
      const headers: Record<string, string> = {};
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch('/api/running/sync', {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to sync activities');
      }

      const data = await response.json();

      if (data.success) {
        setSyncResult({
          activitiesSynced: data.activitiesSynced,
          activitiesDeleted: data.activitiesDeleted,
          isFirstSync: data.isFirstSync,
          syncPeriodDays: data.syncPeriodDays,
        });

        await loadActivities();
      } else {
        setError(data.errors?.join(', ') || 'Sync failed');
      }
    } catch (err) {
      console.error('Error syncing activities:', err);
      setError('Failed to sync activities. Please try again.');
    } finally {
      setIsSyncLoading(false);
    }
  };

  const handleValidate = async (group: RunGroup, groupIndex: number) => {
    setMergeStatuses((prev) => ({
      ...prev,
      [groupIndex]: { groupIndex, status: 'validating', step: 'Validating merge (dry run)...' },
    }));

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const activityIds = group.activities.map((a) => a.activityId);

      const response = await fetch('/api/running/merge', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          activityIds,
          dryRun: true,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
          const errorList = errorData.errors.map((err: string) => `\u2022 ${err}`).join('\n');
          throw new Error(errorList);
        }

        let errorMessage = errorData.error || 'Failed to validate merge';
        if (errorData.message) errorMessage = errorData.message;

        if (errorData.details) {
          if (Array.isArray(errorData.details)) {
            const failed = errorData.details.filter((d: any) => !d.success);
            if (failed.length > 0) {
              errorMessage += ': ' + failed.map((d: any) => `${d.activity} (${d.error})`).join(', ');
            }
          } else if (typeof errorData.details === 'string') {
            errorMessage += ': ' + errorData.details;
          }
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.success && data.dryRun) {
        // Dry run succeeded - now fetch Strava activities to check for matches
        const stravaResponse = await fetch('/api/running/dry-run', {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
        });

        if (stravaResponse.ok) {
          const stravaData = await stravaResponse.json();

          // Find matching group by comparing activity IDs
          const matchingGroup = stravaData.runGroups?.find((g: any) => {
            const gIds = g.activities
              .map((a: any) => a.activityId)
              .sort()
              .join(',');
            const ourIds = [...activityIds].sort().join(',');
            return gIds === ourIds;
          });

          if (matchingGroup?.validation) {
            setRunGroups((prev) =>
              prev.map((g, i) => (i === groupIndex ? { ...g, validation: matchingGroup.validation } : g))
            );
          }
        }

        setMergeStatuses((prev) => ({
          ...prev,
          [groupIndex]: { groupIndex, status: 'idle' },
        }));
      } else {
        setMergeStatuses((prev) => ({
          ...prev,
          [groupIndex]: {
            groupIndex,
            status: 'error',
            error: data.message || 'Dry run validation failed',
          },
        }));
      }
    } catch (err: any) {
      console.error('Error validating group:', err);
      setMergeStatuses((prev) => ({
        ...prev,
        [groupIndex]: {
          groupIndex,
          status: 'error',
          error: err.message || 'Failed to validate merge',
        },
      }));
    }
  };

  const handleMerge = async (group: RunGroup, groupIndex: number) => {
    setMergeStatuses((prev) => ({
      ...prev,
      [groupIndex]: { groupIndex, status: 'merging', step: 'Starting merge...' },
    }));

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const activityIds = group.activities.map((a) => a.activityId);

      const response = await fetch('/api/running/merge', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          activityIds,
          stravaIds: group.validation?.stravaMatches || {},
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.needsRecovery) {
          setMergeStatuses((prev) => ({
            ...prev,
            [groupIndex]: {
              groupIndex,
              status: 'error',
              error: data.error,
              step: data.step,
              canRetry: data.canRetry,
              needsRecovery: true,
              missingIds: data.missingIds,
            },
          }));
        } else {
          setMergeStatuses((prev) => ({
            ...prev,
            [groupIndex]: {
              groupIndex,
              status: 'error',
              error: data.error || 'Merge failed',
              step: data.step,
              canRetry: data.canRetry,
            },
          }));
        }
        return;
      }

      setMergeStatuses((prev) => ({
        ...prev,
        [groupIndex]: { groupIndex, status: 'success' },
      }));

      setTimeout(() => {
        setRunGroups((prev) => prev.filter((_g, i) => i !== groupIndex));
        setMergeStatuses((prev) => {
          const newStatuses = { ...prev };
          delete newStatuses[groupIndex];
          return newStatuses;
        });
      }, 3000);
    } catch (err) {
      console.error('Error merging activities:', err);
      setMergeStatuses((prev) => ({
        ...prev,
        [groupIndex]: {
          groupIndex,
          status: 'error',
          error: 'Failed to merge activities',
          canRetry: true,
        },
      }));
    }
  };

  const _handleRecovery = async (group: RunGroup, groupIndex: number) => {
    const mergeStatus = mergeStatuses[groupIndex];
    if (!mergeStatus?.missingIds) return;

    setMergeStatuses((prev) => ({
      ...prev,
      [groupIndex]: { ...prev[groupIndex], status: 'recovery', step: 'Recovering activities...' },
    }));

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const token = localStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const activityIds = group.activities.map((a) => a.activityId);

      const response = await fetch('/api/running/recover', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          activityIds,
          stravaIds: group.validation?.stravaMatches || {},
          missingStravaIds: mergeStatus.missingIds,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setMergeStatuses((prev) => ({
          ...prev,
          [groupIndex]: {
            ...prev[groupIndex],
            status: 'error',
            error: `Recovery failed: ${data.error}`,
          },
        }));
        return;
      }

      setMergeStatuses((prev) => ({
        ...prev,
        [groupIndex]: { groupIndex, status: 'success' },
      }));

      setTimeout(() => {
        setMergeStatuses((prev) => {
          const newStatuses = { ...prev };
          delete newStatuses[groupIndex];
          return newStatuses;
        });
      }, 3000);
    } catch (err) {
      console.error('Error recovering activities:', err);
      setMergeStatuses((prev) => ({
        ...prev,
        [groupIndex]: {
          ...prev[groupIndex],
          status: 'error',
          error: 'Failed to recover activities',
        },
      }));
    }
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    const dateStr = date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });

    const tzAbbr =
      new Date()
        .toLocaleTimeString('en-US', {
          timeZoneName: 'short',
        })
        .split(' ')
        .pop() || '';

    return `${dateStr} (${tzAbbr})`;
  };

  const formatTime = (dateString: string) => {
    // startTimeLocal is "YYYY-MM-DD HH:MM:SS" - parse as local time
    const normalized = dateString.replace(' ', 'T');
    const date = new Date(normalized);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDistance = (meters?: number) => {
    if (!meters) return 'N/A';
    const km = meters / 1000;
    return `${km.toFixed(2)} km`;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    }
    return `${minutes}m ${secs}s`;
  };

  const formatPace = (metersPerSecond?: number) => {
    if (!metersPerSecond || metersPerSecond === 0) return 'N/A';
    const minutesPerKm = 1000 / (metersPerSecond * 60);
    const minutes = Math.floor(minutesPerKm);
    const seconds = Math.floor((minutesPerKm - minutes) * 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')} /km`;
  };

  const getActivityTypeIcon = (typeKey?: string) => {
    if (!typeKey) return '🏃';
    if (typeKey.toLowerCase().includes('track')) return '🏟️';
    return '🏃';
  };

  const getMergeGroupForDate = (date: string): RunGroup | undefined => {
    return runGroups.find((group) => group.date === date);
  };

  const isActivityInMergeGroup = (activityId: number): boolean => {
    for (const group of runGroups) {
      if (group.activities.some((a) => a.activityId === activityId)) {
        return true;
      }
    }
    return false;
  };

  const getMergeGroupIndex = (group: RunGroup): number => {
    return runGroups.findIndex((g) => {
      const gIds = g.activities
        .map((a) => a.activityId)
        .sort()
        .join(',');
      const targetIds = group.activities
        .map((a) => a.activityId)
        .sort()
        .join(',');
      return gIds === targetIds;
    });
  };

  return (
    <div className="running-tab">
      <div className="running-tab__actions">
        <button
          onClick={handleSync}
          disabled={!isConnected || isSyncing || isSyncLoading}
          className="running-tab__button running-tab__button--sync"
          title={!isConnected ? 'Connect all services to enable' : 'Sync running activities from Garmin Connect'}
        >
          <span className="running-tab__button-icon">{isSyncLoading ? '⏳' : '🔄'}</span>
          <span className="running-tab__button-text">{isSyncLoading ? 'Syncing...' : 'Sync'}</span>
        </button>
      </div>

      {error && (
        <div className="running-tab__error">
          <p>{error}</p>
        </div>
      )}

      {syncResult && (
        <div className="running-tab__sync-result">
          <div className="running-tab__sync-result-icon">✅</div>
          <div className="running-tab__sync-result-content">
            <div className="running-tab__sync-result-title">Sync Complete!</div>
            <div className="running-tab__sync-result-details">
              {syncResult.isFirstSync && <p>Initial sync completed (last {syncResult.syncPeriodDays} days)</p>}
              {!syncResult.isFirstSync && <p>Synced last {syncResult.syncPeriodDays} days</p>}
              {syncResult.activitiesSynced > 0 && <p>{syncResult.activitiesSynced} activities synced</p>}
              {syncResult.activitiesDeleted > 0 && <p>{syncResult.activitiesDeleted} old activities removed</p>}
            </div>
          </div>
        </div>
      )}

      {isActivitiesLoading ? (
        <div className="running-tab__loading">
          <span className="running-tab__loading-icon">⏳</span>
          <p>Loading activities...</p>
        </div>
      ) : activityGroups.length > 0 ? (
        <div className="running-tab__results">
          <h3 className="running-tab__section-title">
            📊 Running Activities ({activityGroups.reduce((sum, group) => sum + group.activities.length, 0)} total)
          </h3>

          {activityGroups.map((group) => {
            const mergeGroup = getMergeGroupForDate(group.date);
            const mergeGroupIndex = mergeGroup ? getMergeGroupIndex(mergeGroup) : -1;
            const mergeStatus = mergeGroupIndex >= 0 ? mergeStatuses[mergeGroupIndex] : undefined;

            return (
              <div key={group.date} className="running-tab__date-group">
                <h4 className="running-tab__date-header">{formatDate(group.date)}</h4>

                {mergeGroup && (
                  <div className="running-tab__merge-controls">
                    {mergeGroup.mergedStravaActivity ? (
                      <div className="running-tab__merged-activity">
                        <div className="running-tab__merged-activity-header">
                          <span className="running-tab__merged-activity-icon">✅</span>
                          <div className="running-tab__merged-activity-title">
                            <div className="running-tab__merged-activity-name">
                              {mergeGroup.mergedStravaActivity.name}
                            </div>
                            <div className="running-tab__merged-activity-subtitle">Already merged in Strava</div>
                          </div>
                        </div>

                        <div className="running-tab__merged-activity-stats">
                          <div className="running-tab__stat">
                            <div className="running-tab__stat-label">Distance</div>
                            <div className="running-tab__stat-value">
                              {formatDistance(mergeGroup.mergedStravaActivity.distance)}
                            </div>
                          </div>

                          <div className="running-tab__stat">
                            <div className="running-tab__stat-label">Duration</div>
                            <div className="running-tab__stat-value">
                              {formatDuration(mergeGroup.mergedStravaActivity.duration)}
                            </div>
                          </div>

                          {mergeGroup.mergedStravaActivity.averageSpeed && (
                            <div className="running-tab__stat">
                              <div className="running-tab__stat-label">Pace</div>
                              <div className="running-tab__stat-value">
                                {formatPace(mergeGroup.mergedStravaActivity.averageSpeed)}
                              </div>
                            </div>
                          )}

                          {mergeGroup.mergedStravaActivity.averageHR && (
                            <div className="running-tab__stat">
                              <div className="running-tab__stat-label">Avg HR</div>
                              <div className="running-tab__stat-value">
                                {Math.round(mergeGroup.mergedStravaActivity.averageHR)} bpm
                              </div>
                            </div>
                          )}

                          {mergeGroup.mergedStravaActivity.maxHR && (
                            <div className="running-tab__stat">
                              <div className="running-tab__stat-label">Max HR</div>
                              <div className="running-tab__stat-value">
                                {Math.round(mergeGroup.mergedStravaActivity.maxHR)} bpm
                              </div>
                            </div>
                          )}

                          {mergeGroup.mergedStravaActivity.elevationGain !== undefined && (
                            <div className="running-tab__stat">
                              <div className="running-tab__stat-label">Elevation</div>
                              <div className="running-tab__stat-value">
                                {Math.round(mergeGroup.mergedStravaActivity.elevationGain)} m
                              </div>
                            </div>
                          )}

                          {mergeGroup.mergedStravaActivity.calories && (
                            <div className="running-tab__stat">
                              <div className="running-tab__stat-label">Calories</div>
                              <div className="running-tab__stat-value">{mergeGroup.mergedStravaActivity.calories}</div>
                            </div>
                          )}

                          {mergeGroup.mergedStravaActivity.averageCadence && (
                            <div className="running-tab__stat">
                              <div className="running-tab__stat-label">Cadence</div>
                              <div className="running-tab__stat-value">
                                {Math.round(mergeGroup.mergedStravaActivity.averageCadence)} spm
                              </div>
                            </div>
                          )}

                          {mergeGroup.mergedStravaActivity.averageWatts && (
                            <div className="running-tab__stat">
                              <div className="running-tab__stat-label">Avg Power</div>
                              <div className="running-tab__stat-value">
                                {Math.round(mergeGroup.mergedStravaActivity.averageWatts)} W
                              </div>
                            </div>
                          )}

                          {mergeGroup.mergedStravaActivity.sufferScore && (
                            <div className="running-tab__stat">
                              <div className="running-tab__stat-label">Suffer Score</div>
                              <div className="running-tab__stat-value">
                                {mergeGroup.mergedStravaActivity.sufferScore}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="running-tab__merge-info">
                          🔀 Merge available: {mergeGroup.activities.length} consecutive runs detected
                        </div>

                        {mergeStatus?.status === 'validating' ? (
                          <div className="running-tab__merge-status running-tab__merge-status--loading">
                            <span className="running-tab__merge-icon">⏳</span>
                            <span className="running-tab__merge-text">{mergeStatus.step}</span>
                          </div>
                        ) : mergeStatus?.status === 'merging' ? (
                          <div className="running-tab__merge-status running-tab__merge-status--loading">
                            <span className="running-tab__merge-icon">⏳</span>
                            <span className="running-tab__merge-text">{mergeStatus.step || 'Merging...'}</span>
                          </div>
                        ) : mergeStatus?.status === 'success' ? (
                          <div className="running-tab__merge-status running-tab__merge-status--success">
                            <span className="running-tab__merge-icon">✅</span>
                            <span className="running-tab__merge-text">Successfully merged!</span>
                          </div>
                        ) : (
                          <>
                            {mergeStatus?.status === 'error' && (
                              <div className="running-tab__merge-status running-tab__merge-status--error">
                                <span className="running-tab__merge-icon">❌</span>
                                <div className="running-tab__merge-error-text" style={{ whiteSpace: 'pre-line' }}>
                                  {mergeStatus.error}
                                </div>
                              </div>
                            )}

                            <div className="running-tab__group-actions">
                              <button
                                onClick={() => handleValidate(mergeGroup, mergeGroupIndex)}
                                disabled={!isConnected || isSyncing}
                                className="running-tab__button running-tab__button--secondary"
                                title="Test merge - downloads and validates FIT files without modifying Strava"
                              >
                                <span className="running-tab__button-icon">🔍</span>
                                <span className="running-tab__button-text">Dry Run</span>
                              </button>

                              <button
                                onClick={() => handleMerge(mergeGroup, mergeGroupIndex)}
                                disabled={!isConnected || isSyncing || !mergeGroup.validation?.allClearedFromStrava}
                                className="running-tab__button running-tab__button--primary"
                                title={
                                  !mergeGroup.validation
                                    ? 'Run Dry Run first to validate'
                                    : !mergeGroup.validation.allClearedFromStrava
                                      ? 'Delete original runs from Strava first'
                                      : `Merge these ${mergeGroup.activities.length} runs into a single activity`
                                }
                              >
                                <span className="running-tab__button-icon">🔀</span>
                                <span className="running-tab__button-text">Merge</span>
                              </button>
                            </div>
                          </>
                        )}

                        {mergeGroup.validation && (
                          <div className="running-tab__merge-validation">
                            {mergeGroup.validation.allClearedFromStrava ? (
                              <span className="running-tab__merge-validation--success">
                                ✅ All {mergeGroup.activities.length} runs cleared from Strava — ready to merge
                              </span>
                            ) : (
                              <span className="running-tab__merge-validation--error">
                                ❌ Delete {mergeGroup.activities.length} original runs from Strava before merging
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="running-tab__activities">
                  {[...group.activities]
                    .sort((a, b) => new Date(a.startTimeLocal).getTime() - new Date(b.startTimeLocal).getTime())
                    .map((activity) => {
                      const isInMergeGroup = isActivityInMergeGroup(activity.activityId);
                      const isAlreadyMerged = mergeGroup?.mergedStravaActivity && isInMergeGroup;
                      return (
                        <div
                          key={activity.activityId}
                          className={`running-tab__activity-card ${isInMergeGroup ? 'running-tab__activity-card--highlight' : ''} ${isAlreadyMerged ? 'running-tab__activity-card--merged' : ''}`}
                        >
                          <div className="running-tab__activity-card-header">
                            <span className="running-tab__activity-icon">
                              {getActivityTypeIcon(activity.activityTypeKey)}
                            </span>
                            <div className="running-tab__activity-title">
                              <div className="running-tab__activity-name">{activity.activityName}</div>
                              <div className="running-tab__activity-time">{formatTime(activity.startTimeLocal)}</div>
                            </div>
                          </div>

                          <div className="running-tab__activity-stats">
                            <div className="running-tab__stat">
                              <div className="running-tab__stat-label">Distance</div>
                              <div className="running-tab__stat-value">{formatDistance(activity.distance)}</div>
                            </div>

                            <div className="running-tab__stat">
                              <div className="running-tab__stat-label">Duration</div>
                              <div className="running-tab__stat-value">{formatDuration(activity.duration)}</div>
                            </div>

                            {activity.averageHR && (
                              <div className="running-tab__stat">
                                <div className="running-tab__stat-label">Avg HR</div>
                                <div className="running-tab__stat-value">{activity.averageHR} bpm</div>
                              </div>
                            )}

                            {activity.elevationGain !== undefined && (
                              <div className="running-tab__stat">
                                <div className="running-tab__stat-label">Elevation</div>
                                <div className="running-tab__stat-value">{Math.round(activity.elevationGain)} m</div>
                              </div>
                            )}

                            {activity.calories && (
                              <div className="running-tab__stat">
                                <div className="running-tab__stat-label">Calories</div>
                                <div className="running-tab__stat-value">{activity.calories}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      ) : !error ? (
        <div className="running-tab__empty">
          <span className="running-tab__empty-icon">🏃</span>
          <p className="running-tab__empty-text">No activities yet</p>
          <p className="running-tab__empty-subtext">
            Click &quot;Sync&quot; to load your running activities from Garmin Connect
          </p>
        </div>
      ) : null}
    </div>
  );
};
